/**
 * <kismet-product-rating> — small stars + review count badge that
 * pulls the real average score from Yotpo's Storefront API v3.
 *
 * Attributes:
 *   data-app-key      Yotpo App Key (the loader GUID).
 *   data-product-id   Shopify product ID.
 *
 * Renders nothing if the product has no reviews yet (hides the host).
 */

(function () {
  if (customElements.get('kismet-product-rating')) return;

  const STAR =
    '<svg viewBox="0 0 21 21" aria-hidden="true"><path fill="currentColor" d="M10.5 0.5l2.78 6.55 7.07.6-5.37 4.65 1.61 6.92L10.5 15.6 4.41 19.22l1.61-6.92L.65 7.65l7.07-.6z"/></svg>';

  // The Product schema ships with an aggregateRating already, rendered from
  // the reviews.* metafields (head.structured-data.product.liquid). Yotpo
  // syncs those a few hours behind, so rewrite the block in place when the
  // live numbers differ: Google requires the marked-up rating to match the
  // stars on screen, and those come from this same fetch. Rewriting beats
  // appending a second Product block — two blocks sharing an @id but
  // disagreeing on the rating is exactly the ambiguity we want to avoid.
  function syncAggregateRatingSchema(avg, total) {
    const script = document.querySelector('script[data-kismet-product-schema]');
    if (!script) return;
    // Schema.org requires ratingValue >= worstRating (1). If Yotpo returned
    // a 0/null averageScore alongside totalReview > 0 (malformed response),
    // leave the server-rendered value alone rather than overwrite it with an
    // invalid AggregateRating that Google's validator would reject.
    if (!avg || avg < 1 || !total) return;

    let data;
    try {
      data = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!data || !data['@id']) return;

    const ratingValue = Number(avg.toFixed(1));
    const current = data.aggregateRating;
    if (current && current.ratingValue === ratingValue && current.ratingCount === total) return;

    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingValue,
      ratingCount: total,
      bestRating: 5,
      worstRating: 1
    };
    script.textContent = JSON.stringify(data);
  }

  class KismetProductRating extends HTMLElement {
    // /collections renders 27 of these across six carousel rows. Firing every
    // Yotpo request on connect put 27 XHRs on the critical path for rows the
    // visitor may never scroll to, so the badge hydrates when it nears the
    // viewport instead. The :not([data-ready]) CSS already reserves its space,
    // so waiting costs no layout shift. On the PDP there is a single badge
    // above the fold, which the observer resolves on its first callback —
    // including the aggregateRating schema injection, which only runs there.
    connectedCallback() {
      if (this._hydrated) return;
      if (typeof IntersectionObserver !== 'function') { this._hydrate(); return; }
      // The product page carries a Product schema script, and this component is
      // what reconciles its aggregateRating with the live numbers. There is
      // exactly one badge there, so gating it would buy nothing while tying the
      // freshness of structured data to where the badge happens to sit in the
      // layout. Hydrate immediately there; only the many-badge pages
      // (collections, landings) wait for visibility.
      if (document.querySelector('script[data-kismet-product-schema]')) { this._hydrate(); return; }
      // The margin is horizontal as well as vertical: the rows are scroll-snap
      // carousels, so a card is just as likely to be off-screen sideways as
      // below. Without it a swipe pops the badge in after the card has already
      // arrived.
      this._observer = new IntersectionObserver((entries, obs) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        obs.disconnect();
        this._observer = null;
        this._hydrate();
      }, { rootMargin: '800px 400px' });
      this._observer.observe(this);
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    _hydrate() {
      if (this._hydrated) return;
      this._hydrated = true;
      this._fetch()
        .then((data) => {
          // Only remove from DOM when Yotpo explicitly says there are
          // zero reviews (or the response is well-formed but empty).
          // Removing on transient errors would silently hide ratings
          // for every visitor whose network blocks api-cdn.yotpo.com.
          if (data && (data.totalReview === 0 || data.total_review === 0)) {
            this.remove();
            return;
          }
          if (!data || (!data.totalReview && !data.total_review)) {
            // Unknown response shape — leave element in place but unrendered.
            // The :not([data-ready]) CSS keeps it visibility:hidden so the
            // trust row's flex layout doesn't shift.
            return;
          }
          const total = data.totalReview || data.total_review;
          const avg = data.averageScore || data.average_score || 0;
          this._render(avg, total);
        })
        .catch((err) => {
          // Transient failure (network, CORS, ad-blocker). Leave the
          // element in place but don't render — trust-row layout stays
          // intact via :not([data-ready]) CSS. Schema injection just
          // doesn't happen this load; Google picks it up on next crawl.
          console.warn('[kismet-product-rating] fetch failed:', err);
        });
    }

    async _fetch() {
      const appKey = this.getAttribute('data-app-key');
      const pid = this.getAttribute('data-product-id');
      if (!appKey || !pid) return null;
      // Bottomline is included in the reviews response — fetch a
      // minimal page (per_page=1) to keep the payload tiny.
      const url =
        'https://api-cdn.yotpo.com/v3/storefront/store/' +
        encodeURIComponent(appKey) +
        '/product/' +
        encodeURIComponent(pid) +
        '/reviews?per_page=1';
      const r = await fetch(url, { credentials: 'omit' });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      const d = await r.json();
      return d.bottomline || null;
    }

    _render(avg, total) {
      const score = Math.max(0, Math.min(5, avg || 0));
      const fillPct = (score / 5) * 100;
      const fmtTotal = (total || 0).toLocaleString();
      // data-count="short" → "(367)" instead of "(367 reviews)" — the compact
      // card badges (client 2026-08-10); the PDP keeps the full wording.
      const countText =
        this.getAttribute('data-count') === 'short'
          ? '(' + fmtTotal + ')'
          : '(' + fmtTotal + ' reviews)';
      this.innerHTML =
        '<span class="kpr-stars" role="img" aria-label="' +
          score.toFixed(1) + ' out of 5 stars based on ' + fmtTotal + ' reviews">' +
          '<span class="kpr-stars__track" aria-hidden="true">' + STAR.repeat(5) + '</span>' +
          '<span class="kpr-stars__fill" style="width:' + fillPct + '%" aria-hidden="true">' +
            STAR.repeat(5) +
          '</span>' +
        '</span>' +
        '<span class="kpr-score" aria-hidden="true">' + score.toFixed(1) + '</span>' +
        // the space keeps the rendered text "4.9 (602)" identical to the
        // start of the aria-label (flex layout doesn't paint it)
        ' ' +
        '<span class="kpr-count">' + countText + '</span>';
      this.setAttribute('data-ready', 'true');

      // SEO: reconcile the server-rendered AggregateRating with the live
      // Yotpo numbers this fetch just returned.
      syncAggregateRatingSchema(score, total);

      // Make the badge a clickable scroll-to-reviews trigger. We target
      // the <kismet-reviews> custom element rather than a section class
      // so it works regardless of the section's Shopify-generated id.
      this.setAttribute('role', 'button');
      this.setAttribute('tabindex', '0');
      // The accessible name starts with the visible text ("4.9 (578)") so
      // speech users can activate it by what they see (WCAG 2.5.3).
      this.setAttribute('aria-label',
        score.toFixed(1) + ' ' + countText + ': ' + score.toFixed(1) + ' out of 5 stars from ' + fmtTotal + ' reviews. Opens the reviews.');
      this.addEventListener('click', this._scrollToReviews);
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._scrollToReviews();
        }
      });
    }

    _scrollToReviews() {
      // Target the SECTION wrapper (`.yotpo-reviews` div, which contains
      // the eyebrow + heading + carousel) so the scroll lands above the
      // section title, not above the first review card. Fall back to the
      // `<kismet-reviews>` custom element only if the wrapper is missing
      // (e.g. a landing that mounts the carousel standalone).
      const target =
        document.querySelector('.yotpo-reviews') ||
        document.querySelector('kismet-reviews');
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  customElements.define('kismet-product-rating', KismetProductRating);
})();
